/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import { getRouteInitData, queryFormEditable, State } from '@gh/common-shared';
import {
  DialogService,
  yesAnswer,
  yesNoConfirmation,
  STYLES_FLEX_COLUMN_LAYOUT,
} from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { combineLatest$ } from '@gh/rx';
import { filter$, map$, switchMap$, take$, takeUntil$ } from '@gh/rx/operators';
import { isEmpty, filter } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';

import {
  getQuoteVariationId,
  isQuoteVariationTotal,
  quoteTotalFromDerivedInfo,
  QuoteVariation,
  QuoteVariationTotal,
} from '../../../../resources/hlp-quote.model';
import { HlpQuoteDetailsCommandService } from '../hlp-quote-details-command.service';
import {
  queryQuoteDerivedInfo,
  queryQuoteIsProfitSupported,
  queryQuoteIsProfitVisible,
  queryQuoteVariationFocusedCell,
  queryQuoteVariations, queryQuoteVariationsFilter,
  HlpQuoteDetailsState,
  VariationsFilter,
  QuoteDetailsStateEventType,
  queryQuoteVariationSelection,
} from '../hlp-quote-details.state';
import { HlpQuoteDetailsVariationGridDefinitionService } from './hlp-quote-details-variation-grid-definition.service';
import { emptySelectionModel, SelectionModel, selectionModelAfterRowRemoved } from '@gh/core-data';
import { HlpDetailsBusinessService } from '../../../../resources/hlp-details-business.service';

@Component({
  standalone: false,
  selector:        'gh-hlp-quote-details-variation-list',
  templateUrl:     'hlp-quote-details-variation-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:       [HlpQuoteDetailsVariationGridDefinitionService],
  styles:          [STYLES_FLEX_COLUMN_LAYOUT],
  styleUrls:       ['hlp-quote-details-variation-list.component.scss'],
})
export class HlpQuoteDetailsVariationListComponent implements OnInit, OnDestroy {
  @Input() hlpId: string;
  
  @Output() onVariationsChange = new EventEmitter();

  variations$: Observable<QuoteVariation[]>;
  filters$: Observable<VariationsFilter>;
  commonVariations: QuoteVariation[];
  total$: Observable<QuoteVariationTotal[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  supportsProfit$: Observable<boolean>;
  isProfitVisible$: Observable<boolean>;
  editable$: Observable<boolean>;
  hlpQuoteVarSelectionModel: SelectionModel = emptySelectionModel();
  private selectionSubscription: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute,
    private userContext: BuilderUserContext,
    private changeDetectorRef: ChangeDetectorRef,
    private state: State<HlpQuoteDetailsState>,
    private commands: HlpQuoteDetailsCommandService,
    private dialogService: DialogService,
    private businessService: HlpDetailsBusinessService,
    public variationGridDefinition: HlpQuoteDetailsVariationGridDefinitionService,
  ) {

  }

  ngOnInit(): void {
    const { variationGridLayout } = getRouteInitData(this.route);

    this.variationGridDefinition.bind(variationGridLayout);

    this.supportsProfit$ = this.state.select(queryQuoteIsProfitSupported);
    this.isProfitVisible$ = this.state.select(queryQuoteIsProfitVisible);

    this.filters$ = this.state.select(queryQuoteVariationsFilter);
    this.variations$ = combineLatest$([this.filters$, this.state.select(queryQuoteVariations)]).pipe(
      map$(([{ search }, variations]) => {
        if (!search) {
          return variations;
        } else {
          return filter(variations, (variation: any) =>
            variation.description.toLowerCase().includes(search.toLowerCase()) ||
            variation.itemCode.toLowerCase().includes(search.toLowerCase()));
        }
      }));
    this.total$ = combineLatest$([this.isProfitVisible$, this.state.select(queryQuoteDerivedInfo)]).pipe(
      map$(([isProfitVisible, info]) => [quoteTotalFromDerivedInfo(info, isProfitVisible)]));
    this.focusedCell$ = this.state.select(queryQuoteVariationFocusedCell);
    this.editable$ = this.state.select(queryFormEditable);
    
    const savedSelectionFromService = this.hlpId && this.businessService.quoteVariationSelectionModel.get(this.hlpId);
    if (savedSelectionFromService) {
      this.hlpQuoteVarSelectionModel = savedSelectionFromService;
      this.commands.setVariationSelection(savedSelectionFromService);
    } else {
      const savedSelection = this.state.project(queryQuoteVariationSelection);
      if (savedSelection) {
        this.hlpQuoteVarSelectionModel = savedSelection;
        if (this.hlpId) {
          this.businessService.quoteVariationSelectionModel.set(this.hlpId, savedSelection);
        }
      } else {
        this.hlpQuoteVarSelectionModel = emptySelectionModel();
      }
    }
    
    this.selectionSubscription = this.state.select(queryQuoteVariationSelection).subscribe((savedSelection) => {
      if (savedSelection) {
        this.hlpQuoteVarSelectionModel = savedSelection;
        this.changeDetectorRef.markForCheck();
      } else {
        this.hlpQuoteVarSelectionModel = emptySelectionModel();
        this.changeDetectorRef.markForCheck();
      }
    });
    
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.selectionSubscription) {
      this.selectionSubscription.unsubscribe();
    }
  }

  onClone(): void {
    this.commands.cloneVariations(() => this.onVariationsChange.emit());
  }

  onAdd(): void {
    this.commands.addVariations(this.hlpId, () => this.onVariationsChange.emit());
  }

  onMoveCreditDebit(variation: QuoteVariation): void {
    this.commands.moveVariationCreditDebit(getQuoteVariationId(variation));
    this.onVariationsChange.emit();
  }

  onViewMemo(variation: QuoteVariation): void {
    this.commands.viewVariationMemo(getQuoteVariationId(variation));
  }
  onhlpCloneQuote(variation: QuoteVariation): void {
      this.commands.hlpCloneQuote(getQuoteVariationId(variation),() => this.onVariationsChange.emit());
    }
  

  onRemove(variation: QuoteVariation): void {
    const id = getQuoteVariationId(variation);
    this.commands.removeVariation(id).pipe(
      takeUntil$(this.destroy$),
    ).subscribe(() => {
      this.onVariationsChange.emit();
      const rows = (this.state.project(queryQuoteVariations) || []).filter(
        (v) => !isQuoteVariationTotal(v),
      );
      const next = selectionModelAfterRowRemoved(
        this.hlpQuoteVarSelectionModel,
        id,
        rows.length === 0,
      );
      if (next !== this.hlpQuoteVarSelectionModel) {
        this.hlpQuoteVarSelectionModel = next;
        this.commands.setVariationSelection(next);
        if (this.hlpId) {
          this.businessService.quoteVariationSelectionModel.set(this.hlpId, next);
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  canMoveToDebit(variation: QuoteVariation): boolean {
    return variation.isCredit && variation.itemPrice > 0;
  }

  canMoveToCredit({ isCredit, origQuantity, quantity, itemPrice }: QuoteVariation): boolean {
    return !isCredit && origQuantity > 0 && quantity <= origQuantity && itemPrice > 0;
  }

  hasMemo(variation: QuoteVariation): boolean {
    return !isEmpty(variation.memo);
  }

  isVariation(variation: QuoteVariation): boolean {
    return !isQuoteVariationTotal(variation);
  }

  onSearch(event: string): void {
    this.commands.setVariationListFilter({
      search: event,
    });
  }

  onHlpQuoteVarSelectionChanged(model: SelectionModel): void {
    this.commands.setVariationSelection(model);
    if (this.hlpId) {
      this.businessService.quoteVariationSelectionModel.set(this.hlpId, model);
    }
  }

  getSelectedVariations(): Observable<QuoteVariation[]> {
    return this.variations$.pipe(
      map$((variations) => {
        if (!variations || variations.length === 0) {
          return [];
        }
        
        if (this.hlpQuoteVarSelectionModel.isExcludeMode) {
          return variations.filter((variation) => {
            const id = getQuoteVariationId(variation);
            return !isQuoteVariationTotal(variation) && !this.hlpQuoteVarSelectionModel.idList.includes(id);
          });
        } else {
          return variations.filter((variation) => {
            const id = getQuoteVariationId(variation);
            return !isQuoteVariationTotal(variation) && this.hlpQuoteVarSelectionModel.idList.includes(id);
          });
        }
      }));
  }

  hasSelectedItems(): Observable<boolean> {
    return this.getSelectedVariations().pipe(
      map$((selectedVariations) => selectedVariations.length > 0)
    );
  }

  onDeleteSelected(): void {
    this.getSelectedVariations().pipe(
      take$(1),
      filter$((selectedVariations) => selectedVariations.length > 0),
      switchMap$((selectedVariations) => {
        const count = selectedVariations.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_item', { code: selectedVariations[0].itemCode })
          : yesNoConfirmation('msg_bulk_remove_n_items', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedVariations));
      }),
    ).subscribe((selectedVariations) => {
      selectedVariations.forEach((variation) => {
        this.state.mutate({
          type: QuoteDetailsStateEventType.VariationRemove,
          variationId: getQuoteVariationId(variation),
        });
      });

      const emptySelection = emptySelectionModel();
      this.hlpQuoteVarSelectionModel = emptySelection;
      this.commands.setVariationSelection(emptySelection);
      if (this.hlpId) {
        this.businessService.quoteVariationSelectionModel.set(this.hlpId, emptySelection);
      }
      setTimeout(() => {
        this.onFocusedCellChanged();
        this.changeDetectorRef.markForCheck();
      }, 0);
    });
  }

}
